<template>
  <div>
    <CardTable
      :title="title"
      :subTitle="subTitle"
      :columns="table.columns"
      :rows="table.rows"
      :isLoading="table.isLoading"
      :selectable="false"
      :pageSize="serverParams.pageSize"
      :pageNumber="serverParams.pageNumber"
      :totalPage="table.totalPage"
      :totalRecords="table.totalRecords"
      searchText="Cari no. registrasi / member / paket"
      @onPerPageChange="onPerPageChange"
      @onPageChange="onPageChange"
      @onSearch="onSearch"
    >
      <template #toolbar>
        <b-button variant="light-primary" @click="getAll">
          <i class="fas fa-sync-alt" /> Refresh
        </b-button>
      </template>
      <template #info>
        <treeselect
          class="d-inline-block mr-3 mb-2 text-left"
          style="width: 220px"
          placeholder="Semua Status"
          v-model="serverParams.status"
          :options="statusOptions"
          @input="onFilter"
        />
      </template>
      <template #cell(price)="data">
        {{ numberFormat(data.value) }}
      </template>
      <template #cell(createdAt)="data">
        {{ dateFormat(data.value) }}
      </template>
      <template #cell(status)="data">
        <b-badge :variant="statusVariant(data.item.status)">
          {{ data.item.statusLabel }}
        </b-badge>
      </template>
      <template #cell(action)="data">
        <div class="d-flex justify-content-center flex-wrap" style="gap: 6px">
          <b-button
            v-if="canVerify(data.item.status)"
            size="sm"
            variant="success"
            @click="openVerify(data.item)"
          >
            Verifikasi
          </b-button>
          <b-button
            v-if="!isFinal(data.item.status)"
            size="sm"
            variant="outline-primary"
            @click="openChangeMethod(data.item)"
          >
            Ubah Metode
          </b-button>
          <b-button
            v-if="data.item.status == 'PAID'"
            size="sm"
            variant="outline-warning"
            @click="refund(data.item)"
          >
            Refund
          </b-button>
          <b-button
            v-if="canCancel(data.item.status)"
            size="sm"
            variant="outline-danger"
            @click="cancel(data.item)"
          >
            Batalkan
          </b-button>
          <span v-if="isFinal(data.item.status)" class="text-muted">-</span>
        </div>
      </template>
      <template #empty>
        <EmptyTable
          title="Belum ada transaksi dari aplikasi"
          :description="subTitle"
        />
      </template>
    </CardTable>

    <b-modal
      v-model="modalMethod.show"
      title="Ubah Metode Pembayaran"
      :no-close-on-backdrop="true"
      ok-title="Simpan"
      cancel-title="Batal"
      :ok-disabled="modalMethod.saving"
      @ok.prevent="changeMethod"
    >
      <p v-if="modalMethod.item" class="mb-3">
        Sesuaikan metode pembayaran untuk
        <b>{{ modalMethod.item.registrationNumber }}</b>
        ({{ modalMethod.item.memberName }}).
      </p>
      <label class="font-weight-bold">Metode Pembayaran</label>
      <treeselect
        placeholder="Bayar di tempat (tanpa metode)"
        v-model="modalMethod.paymentMethodId"
        :options="paymentMethods"
      />
      <small class="text-muted">
        Kosongkan untuk menandai pembayaran di tempat (resepsionis).
      </small>
    </b-modal>

    <b-modal
      v-model="modalVerify.show"
      title="Verifikasi Pembayaran"
      :no-close-on-backdrop="true"
      ok-title="Ya, Verifikasi"
      cancel-title="Batal"
      ok-variant="success"
      :ok-disabled="modalVerify.saving || !isVerifyValid"
      @ok.prevent="submitVerify"
    >
      <div v-if="modalVerify.item">
        <p class="mb-3">
          Verifikasi pembayaran
          <b>{{ modalVerify.item.registrationNumber }}</b>
          ({{ modalVerify.item.memberName }}).
        </p>
        <b-alert
          v-if="modalVerify.item.isPt"
          show
          variant="warning"
          class="py-2 px-3 mb-3"
        >
          <small>
            <i class="fas fa-id-card mr-1"></i>
            Paket Personal Trainer membutuhkan <b>kartu akses aktif</b>.
            Pastikan kartu member sudah diterbitkan/diaktifkan sebelum
            verifikasi.
          </small>
        </b-alert>
        <div class="d-flex justify-content-between mb-2">
          <span class="text-muted">Harga paket</span>
          <span>{{ numberFormat(modalVerify.item.price) }}</span>
        </div>
        <div class="d-flex justify-content-between align-items-center mb-1">
          <label class="font-weight-bold mb-0">Diskon</label>
          <b-form-radio-group
            v-model="modalVerify.discountType"
            :options="discountTypeOptions"
            buttons
            button-variant="outline-secondary"
            size="sm"
          />
        </div>
        <b-input-group
          :append="modalVerify.discountType === 'PERCENT' ? '%' : 'Rp'"
        >
          <b-form-input
            type="number"
            min="0"
            :max="
              modalVerify.discountType === 'PERCENT'
                ? 100
                : modalVerify.item.price
            "
            v-model.number="modalVerify.discount"
            placeholder="0"
          />
        </b-input-group>
        <small v-if="discountExceeds" class="text-danger">
          {{
            modalVerify.discountType === "PERCENT"
              ? "Diskon tidak boleh melebihi 100%."
              : "Diskon tidak boleh melebihi harga paket."
          }}
        </small>
        <small
          v-else-if="
            modalVerify.discountType === 'PERCENT' && discountRupiah > 0
          "
          class="text-muted d-block"
        >
          Setara potongan {{ numberFormat(discountRupiah) }}.
        </small>
        <hr />
        <div class="d-flex justify-content-between font-weight-bold">
          <span>Total setelah diskon</span>
          <span>{{ numberFormat(verifyTotal) }}</span>
        </div>
        <small class="text-muted d-block mt-2">
          Diskon dipotong dari harga paket. Pajak (bila produk kena pajak)
          dihitung ulang oleh sistem saat transaksi dibuat.
        </small>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { appTransaction as columns } from "@/core/datasource/column";
import { pageSize, dateFormat, numberFormat } from "@/core/utils";

export default {
  data: () => ({
    title: "Transaksi Mobile Apps",
    subTitle:
      "Pendaftaran paket dari Mobile Apps — verifikasi pembayaran, refund, atau batalkan transaksi",
    serverParams: {
      pageNumber: 1,
      pageSize,
      status: null,
      keyword: null,
      orderBy: "",
    },
    statusOptions: [
      { id: "PENDING", label: "Menunggu Pembayaran" },
      { id: "WAITING_VERIFICATION", label: "Menunggu Verifikasi" },
      { id: "PAID", label: "Lunas" },
      { id: "CANCELLED", label: "Dibatalkan" },
      { id: "REFUNDED", label: "Dana Dikembalikan" },
    ],
    table: {
      isLoading: false,
      columns,
      rows: [],
      totalPage: 0,
      totalRecords: 0,
    },
    paymentMethods: [],
    modalMethod: {
      show: false,
      item: null,
      paymentMethodId: null,
      saving: false,
    },
    modalVerify: {
      show: false,
      item: null,
      discount: 0,
      discountType: "RP",
      saving: false,
    },
    discountTypeOptions: [
      { text: "Rp", value: "RP" },
      { text: "%", value: "PERCENT" },
    ],
  }),
  computed: {
    ...mapGetters(["store"]),
    // Potongan efektif dalam Rupiah (mengonversi bila mode persen), dibulatkan
    // dan dibatasi ke harga paket — inilah nilai yang dikirim ke backend.
    discountRupiah() {
      const price = this.modalVerify.item ? this.modalVerify.item.price : 0;
      const input = Number(this.modalVerify.discount) || 0;
      const raw =
        this.modalVerify.discountType === "PERCENT"
          ? (price * input) / 100
          : input;
      return Math.min(Math.max(Math.round(raw), 0), price);
    },
    verifyTotal() {
      const price = this.modalVerify.item ? this.modalVerify.item.price : 0;
      return Math.max(price - this.discountRupiah, 0);
    },
    discountExceeds() {
      if (this.modalVerify.item == null) return false;
      const input = Number(this.modalVerify.discount) || 0;
      return this.modalVerify.discountType === "PERCENT"
        ? input > 100
        : input > this.modalVerify.item.price;
    },
    isVerifyValid() {
      const input = Number(this.modalVerify.discount) || 0;
      return input >= 0 && !this.discountExceeds;
    },
  },
  created() {
    this.getAll();
    this.getPaymentMethods();
  },
  methods: {
    dateFormat,
    numberFormat,
    statusVariant(status) {
      switch (status) {
        case "PENDING":
          return "warning";
        case "WAITING_VERIFICATION":
          return "info";
        case "PAID":
          return "success";
        case "REFUNDED":
          return "dark";
        default:
          return "secondary";
      }
    },
    canVerify(status) {
      return status == "PENDING" || status == "WAITING_VERIFICATION";
    },
    canCancel(status) {
      return status != "CANCELLED" && status != "REFUNDED";
    },
    isFinal(status) {
      return status == "CANCELLED" || status == "REFUNDED";
    },
    getAll() {
      const self = this;
      if (self.store == null) return;

      self.table.isLoading = true;
      self.$store
        .dispatch("apis/get", {
          url: `/transaction/${self.store.id}/app`,
          params: self.serverParams,
        })
        .then((response) => {
          if (response.error) {
            self.$message.error({ zIndex: 10000, message: response.message });
          } else {
            self.table.rows = response.data.data;
            self.table.totalPage = response.data.totalPage;
            self.table.totalRecords = response.data.totalData;
          }
        })
        .finally(() => (self.table.isLoading = false));
    },
    getPaymentMethods() {
      const self = this;
      if (self.store == null) return;

      self.$store
        .dispatch("apis/get", {
          url: `/store/${self.store.id}/payment-method`,
          params: { pageNumber: 1, pageSize: 100, orderBy: "sequenceNumber" },
        })
        .then((response) => {
          if (!response.error) {
            self.paymentMethods = (response.data.data || [])
              .filter((x) => x.actived)
              .map((x) => ({
                id: x.id,
                label: x.group ? `${x.label} (${x.group.label})` : x.label,
              }));
          }
        });
    },
    openVerify(item) {
      this.modalVerify.item = item;
      this.modalVerify.discount = 0;
      this.modalVerify.discountType = "RP";
      this.modalVerify.saving = false;
      this.modalVerify.show = true;
    },
    submitVerify() {
      const self = this;
      if (self.modalVerify.item == null || !self.isVerifyValid) return;

      self.modalVerify.saving = true;
      self.$store
        .dispatch("apis/put", {
          url: `/transaction/${self.store.id}/app/${self.modalVerify.item.id}/verify`,
          params: { discount: self.discountRupiah },
        })
        .then((response) => {
          if (response.error) {
            self.$message.error({ zIndex: 10000, message: response.message });
          } else {
            self.$message.success({ zIndex: 10000, message: response.message });
            self.modalVerify.show = false;
            self.getAll();
          }
        })
        .finally(() => (self.modalVerify.saving = false));
    },
    refund(item) {
      this.doAction(
        item,
        "refund",
        `Kembalikan dana untuk ${item.registrationNumber} (${item.memberName}) ?`,
        "Ya, Refund"
      );
    },
    cancel(item) {
      this.doAction(
        item,
        "cancel",
        `Batalkan transaksi ${item.registrationNumber} (${item.memberName}) ?`,
        "Ya, Batalkan"
      );
    },
    doAction(item, action, confirmText, okText) {
      const self = this;

      self.$dialog
        .confirm(confirmText, { okText, cancelText: "Ga jadi", loader: true })
        .then((dialog) => {
          self.$store
            .dispatch("apis/put", {
              url: `/transaction/${self.store.id}/app/${item.id}/${action}`,
            })
            .then((response) => {
              if (response.error) {
                self.$message.error({
                  zIndex: 10000,
                  message: response.message,
                });
              } else {
                self.$message.success({
                  zIndex: 10000,
                  message: response.message,
                });
                self.getAll();
              }
            })
            .finally(() => dialog.close());
        });
    },
    openChangeMethod(item) {
      this.modalMethod.item = item;
      this.modalMethod.paymentMethodId = item.paymentMethodId || null;
      this.modalMethod.show = true;
    },
    changeMethod() {
      const self = this;
      if (self.modalMethod.item == null) return;

      self.modalMethod.saving = true;
      self.$store
        .dispatch("apis/put", {
          url: `/transaction/${self.store.id}/app/${self.modalMethod.item.id}/payment-method`,
          params: { paymentMethodId: self.modalMethod.paymentMethodId },
        })
        .then((response) => {
          if (response.error) {
            self.$message.error({ zIndex: 10000, message: response.message });
          } else {
            self.$message.success({ zIndex: 10000, message: response.message });
            self.modalMethod.show = false;
            self.getAll();
          }
        })
        .finally(() => (self.modalMethod.saving = false));
    },
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps);
    },
    onPerPageChange(params) {
      this.updateParams({ pageSize: params, pageNumber: 1 });
      this.getAll();
    },
    onPageChange(params) {
      this.updateParams({ pageNumber: params });
      this.getAll();
    },
    onSearch(params) {
      this.updateParams({ keyword: params, pageNumber: 1 });
      this.getAll();
    },
    onFilter() {
      this.updateParams({ pageNumber: 1 });
      this.getAll();
    },
  },
};
</script>
